import React from "react";
import { Helmet } from "react-helmet-async";

export default function Terms() {
  return (
    <>
      {/* SEO */}
      <Helmet>
        <title>Terms & Conditions | Fuzz</title>
        <meta
          name="description"
          content="Review the Terms & Conditions for using Fuzz, including user responsibilities, service limitations, payments, and legal information."
        />
        <link
          rel="canonical"
          href="https://fuzzpreview.myzow.in/terms"
        />
      </Helmet>

      <section className="py-24 bg-white min-h-screen">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">

            {/* Header */}
            <div className="mb-16">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Terms & Conditions
              </h1>
              <p className="text-gray-600 text-lg">
                Effective Date: 1st July 2025
              </p>
              <p className="text-gray-600">
                Entity: Fuzzapp Solutions Private Limited
              </p>
            </div>

            <div className="space-y-12 text-gray-700 leading-relaxed text-base">

              {/* 1 */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  1. User Agreement
                </h2>
                <p>
                  Welcome to https://fuzzapp.io and the Fuzz mobile application ("App").
                  Fuzz and its affiliates offer services subject to this User Agreement.
                  If you do not agree to these terms, please do not use the App or Site.
                  Continued use constitutes acceptance.
                </p>
              </div>

              {/* 2 */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  2. User Account, Password & Security
                </h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li>You are responsible for maintaining account confidentiality.</li>
                  <li>Notify Fuzz immediately of unauthorized access.</li>
                  <li>Log out after each session.</li>
                </ul>
              </div>

              {/* 3 */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  3. Terms of Use
                </h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Comply with all applicable laws.</li>
                  <li>Use Fuzz for lawful, non-commercial purposes.</li>
                  <li>Avoid abusive, fraudulent, or unlawful conduct.</li>
                </ul>
                <p className="mt-4">
                  Fuzz reserves the right to update these terms at any time.
                </p>
              </div>

              {/* 4 */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  4. Copyright & Intellectual Property
                </h2>
                <p>
                  All content including graphics, images, layouts, fonts, and code
                  is owned or licensed by Fuzz. Unauthorized copying or modification
                  is strictly prohibited.
                </p>
              </div>

              {/* 5 */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  5. Use of Site & Products
                </h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Provide accurate purchase details.</li>
                  <li>Maintain account integrity.</li>
                  <li>Accept responsibility for transactions from your account.</li>
                </ul>
              </div>

              {/* 6 */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  6. Purchases & Payments
                </h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Payment details must be accurate and valid.</li>
                  <li>Payments are processed securely.</li>
                  <li>All purchases are final unless required by law.</li>
                </ul>
              </div>

              {/* 7 */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  7. Privacy Policy
                </h2>
                <p>
                  Your data is governed by our Privacy Policy. By using Fuzz,
                  you consent to our data practices as described therein.
                </p>
              </div>

              {/* 8 */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  8. Disclaimer of Warranty
                </h2>
                <p>
                  We do not guarantee uninterrupted or error-free service.
                  Access may be suspended due to maintenance or external factors.
                </p>
              </div>

              {/* 9 */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  9. GPS-Based Services Disclaimer
                </h2>
                <p>
                  Fuzz uses GPS for proximity-based services. Accuracy may vary due to
                  buildings, weather, signal strength, device age, or environmental factors.
                </p>
                <p className="mt-4">
                  Users should not rely solely on GPS data for critical decisions.
                  Fuzz is not liable for inaccuracies.
                </p>
              </div>

              {/* 10 */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  10. Limitation of Liability
                </h2>
                <p>
                  Fuzz is not liable for damages arising from misuse,
                  reliance on app data, or service disruption.
                </p>
              </div>

              {/* 11 */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  11. Feedback & User Content
                </h2>
                <p>
                  User-submitted content becomes the intellectual property of Fuzz
                  and may be used without restriction.
                </p>
              </div>

              {/* 12 */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  12. Termination
                </h2>
                <p>
                  We may suspend or delete accounts for violations of these terms,
                  without prior notice.
                </p>
              </div>

              {/* 13 */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  13. Modifications & Updates
                </h2>
                <p>
                  We may revise these terms at any time. Continued use indicates acceptance.
                </p>
              </div>

              {/* 14 */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  14. Governing Law
                </h2>
                <p>
                  These terms are governed by the laws of India.
                  Courts in Maharashtra shall have exclusive jurisdiction.
                </p>
              </div>

              {/* 15 */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  15. Contact
                </h2>
                <p>Email: support@fuzzapp.io</p>
                <p className="mt-4">
                  Registered Office: 10th Flr, Nesco Tower 4, C WING,
                  Western Express Highway, Goregaon East,
                  Mumbai – 400063, Maharashtra, India
                </p>
              </div>

            </div>

          </div>
        </div>
      </section>
    </>
  );
}